/**
 * Daily approvals & reversals (Reporting, 2026-10-13) -- the eMerge home of the
 * Monday ADMIN DASH widgets Gwynn read her daily numbers from (Monthly
 * Approvals by day, Monthly Reversals).
 *
 * A month of America/New_York days: one row per day, a totals row, a bar
 * chart of approvals by day, approvals by PA, and the cases behind any day in
 * a panel DOCKED beside the table (never a row unfolding inside it). Every
 * number, and every date's source, is the server's (backend
 * services/reporting/dailyOutcomes.js); this screen counts nothing itself.
 * Read-only.
 */
import React, { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DetailDock, useNarrow, useSheetModal } from '../../components/board';
import type { BoardDetailSection } from '../../components/board';
import { DOMAIN, HatchPattern, LivingLegend, ORIGIN, tideAxisLine, tideAxisTick, tideGrid, tideTooltip, useChartMotion } from '../../components/tide';
import {
  DailyApprovalItem, DailyOutcomeDay, DailyOutcomesDayDetail, DailyOutcomesMonth, DailyReversalItem, ReportingResult, reportingApi,
} from '../../services/reportingApi';

const ZONE = 'America/New_York';
type LoadState = 'loading' | 'ready' | 'error' | 'refused';

export function currentMonthInZone(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, year: 'numeric', month: '2-digit' }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${part('year')}-${part('month')}`;
}

export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const isMonth = (v: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

/** A calendar day as words. The day is already New York's; it is formatted in UTC so no zone moves it. */
function dayWords(date: string, style: 'long' | 'short' = 'long'): string {
  const d = new Date(`${date}T00:00:00Z`);
  return new Intl.DateTimeFormat('en-US', style === 'long'
    ? { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
    : { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(d);
}

function monthWords(month: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(new Date(`${month}-01T00:00:00Z`));
}

function money(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Not recorded';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function failure(result: ReportingResult<unknown>): { state: LoadState; message: string } {
  const refused = result.status === 401 || result.status === 403 || /^(AUTHZ_|DENY_)/.test(result.code || '');
  if (refused) return { state: 'refused', message: 'The server declined access to this report. Your access has not changed.' };
  if (result.status && result.status < 500 && /^REPORT_/.test(result.code || '') && result.message) return { state: 'error', message: result.message };
  if (result.status === undefined) return { state: 'error', message: 'Could not reach the reporting service. Nothing was loaded.' };
  return { state: 'error', message: 'The reporting service could not load this report.' };
}

/** The day a clicked bar stands for (recharts hands the entry's fields, and the entry as `payload`). */
function barDate(bar: unknown): string | null {
  const b = bar as { date?: unknown; payload?: { date?: unknown } } | null;
  const date = b && (typeof b.date === 'string' ? b.date : typeof b.payload?.date === 'string' ? b.payload.date : null);
  return date || null;
}

const hasActivity = (d: DailyOutcomeDay) => d.approvals + d.reversals + d.reversals_requested > 0;

/* ── Chips: every date and amount says where it came from ──────────────── */

const BASIS_CHIP: Record<DailyApprovalItem['date_basis'], { label: string; tone: string; means: string }> = {
  ACT_DAY: { label: 'eMerge approval', tone: 'board-tag--ok', means: 'Approved in eMerge (the approval act). Counted on the New York day it was approved.' },
  STATED_DAY: { label: 'eMerge approval · date entered', tone: 'board-tag--ok', means: 'Approved in eMerge for an earlier day the operator entered; counted on that day.' },
  BOARD_DATE: { label: 'Monday board date', tone: 'board-tag--claim', means: 'Monday\'s Approval Date for a case eMerge never approved itself (history before eMerge approvals). Monday\'s claim, not an eMerge act.' },
};

const AMOUNT_SOURCE: Record<string, string> = {
  NATIVE_PRICING_CATALOG: 'Sale amount · price catalog',
  APPROVED_OVERRIDE: 'Sale amount · manager override',
  MONDAY_CLOSED_LEAD_TOTAL: 'Sale amount · Monday Closed Lead Total',
};

const REQUESTER: Record<string, string> = {
  FACILITY: 'the facility', RESPONSIBLE_PARTY: 'the responsible party', DCF: 'DCF', SFS: 'SFS', OTHER: 'other',
};

function Chip({ tone = '', children }: { tone?: string; children: React.ReactNode }) {
  return <span className={`board-tag ${tone}`.trim()}>{children}</span>;
}

function ChipLegend() {
  return (
    <dl className="report-chip-key">
      {(Object.keys(BASIS_CHIP) as Array<keyof typeof BASIS_CHIP>).map((k) => (
        <div key={k}><dt><Chip tone={BASIS_CHIP[k].tone}>{BASIS_CHIP[k].label}</Chip></dt><dd>{BASIS_CHIP[k].means}</dd></div>
      ))}
      <div><dt><Chip>+1 later approval in episode · not counted</Chip></dt><dd>A correction or amendment of this case was approved later. It is the same approval and is not counted again.</dd></div>
      <div><dt><Chip tone="board-tag--danger">Reversal completed</Chip></dt><dd>DCF approved the reversal that day: the claim is reversed. Counted in Reversals and the reversal total.</dd></div>
      <div><dt><Chip>Reversal requested</Chip></dt><dd>Someone asked for the reversal that day. Shown for context; not in the total.</dd></div>
      <div><dt><Chip>{AMOUNT_SOURCE.NATIVE_PRICING_CATALOG}</Chip></dt><dd>The amount is the sale amount of the claim being reversed, and this says where that sale amount came from.</dd></div>
      <div><dt><Chip tone="board-tag--claim">Amount not recorded</Chip></dt><dd>No sale amount is recorded for the reversed claim. It is counted, never added as $0.</dd></div>
      <div><dt><Chip tone="board-tag--claim">Already reversed · amount not added again</Chip></dt><dd>An earlier completed reversal already took this claim's sale back. This one is counted as a reversal; its amount is in the total once, on the first.</dd></div>
      <div><dt><Chip>From the PA case's sale</Chip></dt><dd>The reversed UMED case has no sale of its own, so the amount is its PA case's sale.</dd></div>
    </dl>
  );
}

function ApprovalCard({ a }: { a: DailyApprovalItem }) {
  const chip = BASIS_CHIP[a.date_basis];
  return (
    <li className="report-case">
      <p className="report-case__name">{a.resident || 'Resident not recorded'}</p>
      <dl className="board-detail__facts">
        <div><dt>Building</dt><dd>{a.building || 'Not recorded'}</dd></div>
        <div><dt>PA</dt><dd>{a.pa ? `${a.pa}${a.pa_basis === 'CASE_HOLDER' ? ' (case holder)' : ''}` : 'Not known'}</dd></div>
        <div><dt>Approval date</dt><dd>{dayWords(a.date)}</dd></div>
        {a.approved_by && <div><dt>Approved by</dt><dd>{a.approved_by}</dd></div>}
        {a.board_approved_on && <div><dt>Monday board said</dt><dd>{dayWords(a.board_approved_on)}</dd></div>}
      </dl>
      <p className="report-case__chips">
        <Chip tone={chip.tone}>{chip.label}</Chip>
        {a.later_approvals > 0 && <Chip>+{a.later_approvals} later approval{a.later_approvals === 1 ? '' : 's'} in episode · not counted</Chip>}
      </p>
    </li>
  );
}

function ReversalCard({ r }: { r: DailyReversalItem }) {
  const done = r.kind === 'REVERSAL_COMPLETED';
  return (
    <li className="report-case">
      <p className="report-case__name">{r.resident || 'Resident not recorded'}</p>
      <dl className="board-detail__facts">
        <div><dt>Building</dt><dd>{r.building || 'Not recorded'}</dd></div>
        <div><dt>PA</dt><dd>{r.pa || 'Not known'}</dd></div>
        <div><dt>Requested</dt><dd>{r.requested_on ? `${dayWords(r.requested_on)}${r.requester ? ` by ${REQUESTER[r.requester] || r.requester}` : ''}` : 'Not recorded'}</dd></div>
        <div><dt>Completed</dt><dd>{r.completed_on ? dayWords(r.completed_on) : 'Not yet'}</dd></div>
        <div><dt>Amount</dt><dd className="board-money">{money(r.amount)}{r.already_reversed && r.amount !== null ? ' (not added again)' : ''}</dd></div>
      </dl>
      <p className="report-case__chips">
        <Chip tone={done ? 'board-tag--danger' : ''}>{done ? 'Reversal completed' : 'Reversal requested'}</Chip>
        {r.amount === null
          ? <Chip tone="board-tag--claim">Amount not recorded</Chip>
          : <Chip>{AMOUNT_SOURCE[r.amount_source || ''] || 'Sale amount'}</Chip>}
        {r.amount !== null && r.amount_basis === 'PA_CASE_SALE' && <Chip>From the PA case's sale</Chip>}
        {done && r.already_reversed && <Chip tone="board-tag--claim">Already reversed · amount not added again</Chip>}
      </p>
    </li>
  );
}

function CaseList<T>({ items, empty, render }: { items: T[]; empty: string; render: (item: T, i: number) => React.ReactNode }) {
  if (items.length === 0) return <p className="report-muted">{empty}</p>;
  return <ul className="report-cases">{items.map(render)}</ul>;
}

/* ── The chart: approvals by day, like Monday's widget, split by source ── */

const SERIES = {
  emerge: { key: 'emerge', label: 'eMerge approvals', color: DOMAIN.UMED.color, marker: DOMAIN.UMED.marker },
  board: { key: 'board', label: 'Monday board dates', color: ORIGIN.IMPORT.color, marker: 'hatch' as const },
};

function ApprovalsChart({ report, onOpen }: { report: DailyOutcomesMonth; onOpen: (date: string) => void }) {
  const motion = useChartMotion();
  const uid = useId();
  const hatchId = `${uid.replace(/:/g, '')}-board-hatch`;
  const titleId = `${uid}-title`;
  const data = report.days.map((d) => ({ date: d.date as string, day: Number((d.date as string).slice(8)), emerge: d.approvals_emerge, board: d.approvals_board }));
  const busiest = report.days.reduce<DailyOutcomeDay | null>((best, d) => (!best || d.approvals > best.approvals ? d : best), null);
  const summary = report.totals.approvals === 0
    ? `No approvals in ${monthWords(report.month)}.`
    : `${report.totals.approvals} approvals in ${monthWords(report.month)}: ${report.totals.approvals_emerge} in eMerge, ${report.totals.approvals_board} by Monday board date. Busiest day ${dayWords(busiest?.date as string, 'short')} with ${busiest?.approvals}.`;
  const showBoard = report.totals.approvals_board > 0;
  return (
    <section className="report-section" aria-labelledby={titleId}>
      <div className="report-section-heading">
        <div><p className="report-eyebrow">Monthly approvals</p><h2 id={titleId}>Approvals by day</h2></div>
        <p className="report-scope">Choose a bar to open that day</p>
      </div>
      <div className="report-chart-grid">
        <figure className="report-chart" aria-labelledby={`${uid}-cap`}>
          <div className="report-chart__plot">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="18%">
                <defs><HatchPattern id={hatchId} color={SERIES.board.color} /></defs>
                <CartesianGrid {...tideGrid} vertical={false} />
                <XAxis dataKey="day" tick={tideAxisTick} axisLine={tideAxisLine} tickLine={false} interval="preserveStartEnd" minTickGap={6} />
                <YAxis tick={tideAxisTick} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                <Tooltip
                  {...tideTooltip}
                  labelFormatter={(_l: unknown, payload: Array<{ payload?: { date?: string } }>) => (payload && payload[0]?.payload?.date ? dayWords(payload[0].payload.date) : '')}
                  formatter={(v: number, name: string) => [v, name]}
                />
                <Bar dataKey="emerge" name={SERIES.emerge.label} stackId="a" fill={SERIES.emerge.color} stroke="var(--tide-surface)" strokeWidth={2}
                  radius={showBoard ? [0, 0, 0, 0] : [4, 4, 0, 0]} cursor="pointer" onClick={(bar: unknown) => { const date = barDate(bar); if (date) onOpen(date); }} {...motion} />
                {showBoard && (
                  <Bar dataKey="board" name={SERIES.board.label} stackId="a" fill={`url(#${hatchId})`} stroke={SERIES.board.color} strokeWidth={1}
                    radius={[4, 4, 0, 0]} cursor="pointer" onClick={(bar: unknown) => { const date = barDate(bar); if (date) onOpen(date); }} {...motion} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <figcaption id={`${uid}-cap`} className="report-chart__summary">{summary} The table below has every value.</figcaption>
        </figure>
        <LivingLegend
          stacked
          density="compact"
          title="Approvals"
          items={[
            { key: 'emerge', label: SERIES.emerge.label, color: SERIES.emerge.color, marker: SERIES.emerge.marker, value: report.totals.approvals_emerge, unit: 'this month', description: 'Approved in eMerge (solid).' },
            { key: 'board', label: SERIES.board.label, color: SERIES.board.color, marker: 'hatch', value: report.totals.approvals_board, unit: 'this month', description: 'Monday history, before eMerge approvals (hatched).' },
          ]}
        />
      </div>
    </section>
  );
}

/* ── The page section ──────────────────────────────────────────────────── */

export default function DailyOutcomesReport({ initialMonth }: { initialMonth?: string }) {
  const [month, setMonth] = useState(() => (initialMonth && isMonth(initialMonth) ? initialMonth : currentMonthInZone()));
  const [draft, setDraft] = useState(month);
  const [report, setReport] = useState<DailyOutcomesMonth | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [message, setMessage] = useState('');
  const [openDate, setOpenDate] = useState<string | null>(null);
  const [detail, setDetail] = useState<DailyOutcomesDayDetail | null>(null);
  const [detailState, setDetailState] = useState<LoadState>('loading');
  const [detailMessage, setDetailMessage] = useState('');
  const [exportMessage, setExportMessage] = useState('');
  const [exporting, setExporting] = useState(false);
  const monthRequest = useRef(0);
  const dayRequest = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const opener = useRef<string | null>(null);
  const sheet = useNarrow();
  const splitRef = useRef<HTMLDivElement>(null);
  const uid = useId();

  const load = useCallback(async (m: string) => {
    const id = ++monthRequest.current;
    setState('loading');
    setMessage('');
    setReport(null);
    const result = await reportingApi.dailyOutcomes(m);
    if (id !== monthRequest.current) return;
    if (result.success && result.data) {
      setReport(result.data);
      setState('ready');
      return;
    }
    const f = failure(result);
    setState(f.state);
    setMessage(f.message);
  }, []);

  useEffect(() => { setOpenDate(null); void load(month); }, [load, month]);

  const openable = useMemo(() => (report ? report.days.filter(hasActivity).map((d) => d.date as string) : []), [report]);

  const openDay = useCallback(async (date: string) => {
    opener.current = date;
    setOpenDate(date);
    const id = ++dayRequest.current;
    setDetail(null);
    setDetailState('loading');
    setDetailMessage('');
    const result = await reportingApi.dailyOutcomesDay(date);
    if (id !== dayRequest.current) return;
    if (result.success && result.data) {
      setDetail(result.data);
      setDetailState('ready');
      return;
    }
    const f = failure(result);
    setDetailState(f.state);
    setDetailMessage(f.message);
  }, []);

  const close = useCallback(() => {
    const back = opener.current;
    setOpenDate(null);
    dayRequest.current += 1;
    window.setTimeout(() => {
      const btn = back ? document.getElementById(`${uid}-day-${back}`) : null;
      if (btn) btn.focus();
    }, 0);
  }, [uid]);

  useEffect(() => { if (openDate) headingRef.current?.focus(); }, [openDate]);
  // As a full-screen sheet the dock is modal: the page behind it is inert and does not scroll.
  useSheetModal(Boolean(openDate && sheet), () => (splitRef.current ? splitRef.current.querySelector<HTMLElement>('.board-dock') : null));

  const onKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!openDate) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    const i = openable.indexOf(openDate);
    if (e.altKey && e.key === 'ArrowUp' && i > 0) { e.preventDefault(); void openDay(openable[i - 1]); }
    if (e.altKey && e.key === 'ArrowDown' && i >= 0 && i < openable.length - 1) { e.preventDefault(); void openDay(openable[i + 1]); }
  };

  const applyMonth = (m: string) => {
    if (!isMonth(m)) return;
    setDraft(m);
    setMonth(m);
    setExportMessage('');
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportMessage('');
    const problem = await reportingApi.exportDailyOutcomes(month);
    setExporting(false);
    setExportMessage(problem || '');
  };

  const index = openDate ? openable.indexOf(openDate) : -1;
  const approvals = detail?.approvals || [];
  const completed = (detail?.reversals || []).filter((r) => r.kind === 'REVERSAL_COMPLETED');
  const requested = (detail?.reversals || []).filter((r) => r.kind === 'REVERSAL_REQUESTED');
  const detailBody = (render: () => React.ReactNode) => () => (detailState === 'ready' ? render()
    : detailState === 'loading' ? <p className="report-muted" role="status">Loading the cases…</p>
      : <p className="report-inline-error report-muted" role="alert">{detailMessage}</p>);
  const sections: BoardDetailSection[] = [
    { id: 'approvals', label: `Approvals${detail ? ` (${approvals.length})` : ''}`, render: detailBody(() => <CaseList items={approvals} empty="No approvals this day." render={(a) => <ApprovalCard key={a.umed_case_id} a={a} />} />) },
    { id: 'reversals', label: `Reversals completed${detail ? ` (${completed.length})` : ''}`, render: detailBody(() => <CaseList items={completed} empty="No reversals completed this day." render={(r) => <ReversalCard key={`${r.reversal_id}-c`} r={r} />} />) },
    { id: 'requested', label: `Reversals requested${detail ? ` (${requested.length})` : ''}`, render: detailBody(() => <CaseList items={requested} empty="No reversals requested this day." render={(r) => <ReversalCard key={`${r.reversal_id}-r`} r={r} />} />) },
    { id: 'key', label: 'What the chips mean', render: () => <ChipLegend />, defaultCollapsed: false },
  ];

  const d = report?.definitions;
  return (
    <>
      <section className="report-controls" aria-labelledby={`${uid}-controls`}>
        <h2 id={`${uid}-controls`}>Month</h2>
        <div className="report-control-grid">
          <button type="button" className="report-secondary" onClick={() => applyMonth(shiftMonth(month, -1))}>
            <span aria-hidden="true">&larr;</span> Previous month
          </button>
          <label className="report-date-field">Month (America/New_York days)
            <input type="month" value={draft} onChange={(e) => { setDraft(e.target.value); if (isMonth(e.target.value)) applyMonth(e.target.value); }} />
          </label>
          <button type="button" className="report-secondary" onClick={() => applyMonth(shiftMonth(month, 1))}>
            Next month <span aria-hidden="true">&rarr;</span>
          </button>
          <button type="button" className="report-primary" onClick={() => void exportCsv()} disabled={state !== 'ready' || exporting}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        {!isMonth(draft) && <p className="report-inline-error" role="alert">Choose a month as YYYY-MM.</p>}
        {exportMessage && <p className="report-inline-error" role="alert">{exportMessage}</p>}
      </section>

      {state !== 'ready' && (
        <section className={`report-state report-state-${state}`} aria-live="polite" aria-busy={state === 'loading'}>
          {state === 'loading' ? <p role="status">Loading {monthWords(month)}…</p> : <>
            <h2>{state === 'refused' ? 'Report access declined' : 'Report unavailable'}</h2>
            <p>{message}</p>
            <button type="button" className="report-secondary" onClick={() => void load(month)}>Try again</button>
          </>}
        </section>
      )}

      {state === 'ready' && report && (
        <>
          <section className="report-section" aria-labelledby={`${uid}-totals`}>
            <div className="report-section-heading">
              <div><p className="report-eyebrow">{monthWords(report.month)} · {report.timezone}</p><h2 id={`${uid}-totals`}>Month totals</h2></div>
              <p className="report-generated">Generated {report.generated_at}</p>
            </div>
            <dl className="report-metrics">
              <div className="report-metric"><dt>Approvals</dt><dd>{report.totals.approvals}</dd></div>
              <div className="report-metric"><dt>Reversals (completed)</dt><dd>{report.totals.reversals}</dd></div>
              <div className="report-metric"><dt>Reversal total</dt><dd>{money(report.totals.reversal_total)}</dd></div>
              <div className="report-metric"><dt>Reversals requested</dt><dd>{report.totals.reversals_requested}</dd></div>
              <div className="report-metric"><dt>DCF resubmittals approved (corrected)</dt><dd>{report.totals.resubmittals_approved ?? 0}</dd></div>
              <div className="report-metric"><dt>Daily collections</dt><dd className="report-metric__na">Not available</dd></div>
            </dl>
            <div className="report-device-note" role="note">
              <strong>Daily collections: not available.</strong> {report.collections.reason}
            </div>
            {report.totals.reversals_already_reversed > 0 && (
              <p className="report-muted">{report.totals.reversals_already_reversed} completed reversal{report.totals.reversals_already_reversed === 1 ? ' was' : 's were'} of a claim already reversed; counted, but {report.totals.reversals_already_reversed === 1 ? 'its amount is' : 'their amounts are'} not added to the total again.</p>
            )}
            {report.totals.reversal_amount_missing > 0 && (
              <p className="report-muted">{report.totals.reversal_amount_missing} completed reversal{report.totals.reversal_amount_missing === 1 ? ' has' : 's have'} no sale amount recorded and {report.totals.reversal_amount_missing === 1 ? 'is' : 'are'} not in the reversal total.</p>
            )}
          </section>

          <ApprovalsChart report={report} onOpen={(date) => void openDay(date)} />

          <section className="report-section" aria-labelledby={`${uid}-days`}>
            <div className="report-section-heading">
              <div><p className="report-eyebrow">One row per day</p><h2 id={`${uid}-days`}>Daily approvals &amp; reversals</h2></div>
              <p className="report-scope">Choose a day to see the cases behind it</p>
            </div>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className="board report-outcomes-board" onKeyDown={onKeys}>
              <div className="report-outcomes-split" ref={splitRef}>
                <div className="report-table-scroll report-outcomes-table" role="region" aria-label="Daily approvals and reversals" tabIndex={0}>
                  <table className="report-table report-daily-table report-outcomes">
                    <caption>Approvals and reversals by America/New_York day, {monthWords(report.month)}</caption>
                    <thead><tr>
                      <th scope="col">Date</th><th scope="col">Approvals</th><th scope="col">eMerge</th><th scope="col">Monday board</th>
                      <th scope="col">Reversals</th><th scope="col">Reversal total</th><th scope="col">No amount</th><th scope="col">Already reversed</th><th scope="col">Requested</th>
                      <th scope="col">Resubmittals requested</th><th scope="col">Resubmittals approved</th>
                    </tr></thead>
                    <tbody>{report.days.map((day) => {
                      const date = day.date as string;
                      const active = hasActivity(day);
                      return (
                        <tr key={date} data-open={openDate === date ? 'true' : undefined} className={active ? '' : 'report-row-quiet'}>
                          <th scope="row">
                            {active ? (
                              <button type="button" id={`${uid}-day-${date}`} className="report-day-btn" aria-expanded={openDate === date}
                                aria-controls={openDate === date ? `${uid}-dock` : undefined} onClick={() => void openDay(date)}>
                                {dayWords(date, 'short')}
                              </button>
                            ) : dayWords(date, 'short')}
                          </th>
                          <td>{day.approvals}</td><td>{day.approvals_emerge}</td><td>{day.approvals_board}</td>
                          <td>{day.reversals}</td><td>{day.reversals ? money(day.reversal_total) : '—'}</td>
                          <td>{day.reversal_amount_missing}</td><td>{day.reversals_already_reversed}</td><td>{day.reversals_requested}</td>
                          <td>{day.resubmittals_requested ?? 0}</td><td>{day.resubmittals_approved ?? 0}</td>
                        </tr>
                      );
                    })}</tbody>
                    <tfoot><tr>
                      <th scope="row">Total</th>
                      <td>{report.totals.approvals}</td><td>{report.totals.approvals_emerge}</td><td>{report.totals.approvals_board}</td>
                      <td>{report.totals.reversals}</td><td>{money(report.totals.reversal_total)}</td>
                      <td>{report.totals.reversal_amount_missing}</td><td>{report.totals.reversals_already_reversed}</td><td>{report.totals.reversals_requested}</td>
                      <td>{report.totals.resubmittals_requested ?? 0}</td><td>{report.totals.resubmittals_approved ?? 0}</td>
                    </tr></tfoot>
                  </table>
                </div>
                {openDate && (
                  <DetailDock
                    id={`${uid}-dock`}
                    label={`${dayWords(openDate)}: the cases behind the numbers`}
                    title={<>{dayWords(openDate)}<span className="board-src">{detail ? `${detail.totals.approvals} approvals · ${detail.totals.reversals} reversals · ${money(detail.totals.reversal_total)}` : 'Loading…'}</span></>}
                    index={index + 1}
                    count={openable.length}
                    onPrev={index > 0 ? () => void openDay(openable[index - 1]) : null}
                    onNext={index >= 0 && index < openable.length - 1 ? () => void openDay(openable[index + 1]) : null}
                    onClose={close}
                    sheet={sheet}
                    sections={sections}
                    memoryKey="reports-daily-outcomes"
                    headingRef={headingRef}
                    recordKey={openDate}
                  />
                )}
              </div>
            </div>
          </section>

          <section className="report-section" aria-labelledby={`${uid}-pa`}>
            <div className="report-section-heading">
              <div><p className="report-eyebrow">This month</p><h2 id={`${uid}-pa`}>Approvals by PA</h2></div>
            </div>
            {report.by_pa.length === 0 ? <p className="report-empty-inline">No approvals this month.</p> : (
              <div className="report-table-scroll" role="region" aria-label="Approvals by PA" tabIndex={0}>
                <table className="report-table report-daily-table">
                  <caption>Approvals by PA, {monthWords(report.month)}</caption>
                  <thead><tr><th scope="col">PA</th><th scope="col">Approvals</th><th scope="col">of which Monday board dates</th></tr></thead>
                  <tbody>{report.by_pa.map((p) => (
                    <tr key={p.pa_user_id || '(none)'}><th scope="row">{p.pa_user_id ? (p.pa || 'Name not recorded') : 'PA not known'}</th><td>{p.approvals}</td><td>{p.approvals_board}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <p className="report-muted">The PA is the advocate credited with the case's sale; with no sale, the PA case's holder (marked "case holder" in a day's cases).</p>
          </section>

          {d && (
            <section className="report-section" aria-labelledby={`${uid}-how`}>
              <div className="report-section-heading">
                <div><p className="report-eyebrow">Definitions</p><h2 id={`${uid}-how`}>How these numbers are counted</h2></div>
              </div>
              <dl className="report-definitions">
                <div><dt>Approval</dt><dd>{d.approval}</dd></div>
                <div><dt>Approval day</dt><dd>{d.approval_day}</dd></div>
                <div><dt>Corrections and amendments</dt><dd>{d.once_per_episode}</dd></div>
                <div><dt>Before eMerge approvals</dt><dd>{d.board_history}</dd></div>
                <div><dt>Reversal</dt><dd>{d.reversal}</dd></div>
                <div><dt>Reversal total</dt><dd>{d.reversal_amount}</dd></div>
                <div><dt>Monday-only reversals</dt><dd>{d.board_reversals} Not placed on a day: {report.undated_board_reversals}.</dd></div>
                <div><dt>Collections</dt><dd>{d.collections}</dd></div>
                <div><dt>Days</dt><dd>Every day is an America/New_York calendar day.</dd></div>
              </dl>
            </section>
          )}
        </>
      )}
    </>
  );
}
